import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { Style } from 'inlines'
import { RadioButtonGroup } from '@app/components/core/radioButton/RadioButtonGroup'
import { InitiativeFilter } from 'functions/app/initiative/list/filters'
import { Drawer } from '@app/components/blocks/drawer/Drawer'
import { CheckboxGroup } from '@app/components/core/checkbox/CheckboxGroup'
import Row from '@app/components/layout/Row'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import { ListPhaseModel } from 'functions/app/dashboard/supporting-entities/phase/list'
import { ListDevelopmentFormModel } from 'functions/app/dashboard/supporting-entities/development-form/list'
import { useQuery } from '@based/sdk'

const searchMemberOptions = {
  yes: 'yes',
  no: 'no',
  notSpecified: 'notSpecified',
} as const
type SearchMemberOption = keyof typeof searchMemberOptions

export const InitiativeFilterDrawer = ({
  open,
  onClose,
  filters,
  setFilters,
}: {
  open: boolean
  onClose: () => void
  filters: InitiativeFilter
  setFilters: (filters: InitiativeFilter) => void
}) => {
  const { data: phases }: { data?: ListPhaseModel[] } = useQuery('list-phase')
  const { data: developmentForms }: { data?: ListDevelopmentFormModel[] } =
    useQuery('list-development-forms')
  const t = useTranslations().app.discoveryPage.filterDrawer
  const isMobile = useMediaQuery()
  const [selectedPhases, setSelectedPhases] = useState<number[]>([])
  const [searchMemberOption, setSearchMemberOption] =
    useState<SearchMemberOption>('notSpecified')
  const [selectedDevelopmentForm, setSelectedDevelopmentForm] = useState<
    number[]
  >([])

  useEffect(() => {
    if (open) {
      setSelectedPhases(filters?.phase ?? [])
      setSelectedDevelopmentForm(filters?.developmentForm ?? [])
      setSearchMemberOption(
        filters?.searchingForMembers !== null &&
          filters?.searchingForMembers !== undefined
          ? filters?.searchingForMembers
            ? 'yes'
            : 'no'
          : 'notSpecified',
      )
    }
  }, [filters, open])

  const toggleSelectedPhase = (phase: number) => {
    const remove = selectedPhases.includes(phase)

    if (remove) {
      setSelectedPhases((prev) => prev.filter((p) => p !== phase))
    } else {
      setSelectedPhases((prev) => [...prev, phase])
    }
  }

  const toggleSelectedDevelopmentForm = (form: number) => {
    const remove = selectedDevelopmentForm.includes(form)

    if (remove) {
      setSelectedDevelopmentForm((prev) => prev.filter((p) => p !== form))
    } else {
      setSelectedDevelopmentForm((prev) => [...prev, form])
    }
  }

  const onSubmit = useCallback(() => {
    const filters: InitiativeFilter = {
      phase: selectedPhases,
      developmentForm: selectedDevelopmentForm,
    }

    if (searchMemberOption !== 'notSpecified') {
      filters.searchingForMembers = searchMemberOption === 'yes'
    }

    setFilters(filters)
    onClose()
  }, [
    selectedPhases,
    searchMemberOption,
    selectedDevelopmentForm,
    setFilters,
    onClose,
  ])

  return (
    <Drawer title={t.title} open={open} setOpen={onClose} side="left">
      <Column
        style={{ height: '100%', flex: 0, justifyContent: 'space-between' }}
      >
        <div>
          <Title.H5>{t.phase}</Title.H5>
          <PhaseCheckboxGroup
            selected={selectedPhases}
            onToggle={toggleSelectedPhase}
            items={phases || []}
            columns={isMobile ? 1 : 2}
            style={{ marginBottom: '1.5rem' }}
          />
          <Title.H5>
            {t.lookingForMembers}
          </Title.H5>
          <RadioButtonGroup
            labelSize="small"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              justifyContent: isMobile ? 'flex-start' : 'space-between',
              marginBottom: '1.5rem',
            }}
            radioButtonStyle={{
              width: isMobile ? '100%' : '13rem',
            }}
            selectedOption={searchMemberOption}
            options={[
              {
                value: searchMemberOptions.yes,
                label: t.yes,
              },
              {
                value: searchMemberOptions.no,
                label: t.no,
              },
              {
                value: searchMemberOptions.notSpecified,
                label: t.noPreference,
              },
            ]}
            onClick={setSearchMemberOption}
          />
          <Title.H5>
            {t.developmentForm}
          </Title.H5>
          <DevelopmentFormsCheckboxGroup
            items={developmentForms || []}
            selected={selectedDevelopmentForm}
            onToggle={toggleSelectedDevelopmentForm}
            columns={isMobile ? 1 : 2}
          />
        </div>

        <Row
          style={{
            width: '100%',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          <Button onClick={onClose} label={t.back} variant="secondary" />
          <Button onClick={onSubmit} label={t.apply} />
        </Row>
      </Column>
    </Drawer>
  )
}

const PhaseCheckboxGroup = ({
  selected,
  onToggle,
  items,
  columns,
  style,
}: {
  selected: number[]
  onToggle: (value: number) => void
  items: ListPhaseModel[]
  columns?: number
  style?: Style
}) => {
  const t = useTranslations().app.common

  const checkboxOptions = useMemo(() => {
    return items.map((phase) => ({
      label: t.initiativePhases[phase.phaseType],
      value: phase.id,
    }))
  }, [items, t])

  return (
    <CheckboxGroup
      options={checkboxOptions}
      columns={columns}
      selected={selected}
      onToggle={onToggle}
      style={style}
    />
  )
}

const DevelopmentFormsCheckboxGroup = ({
  selected,
  onToggle,
  items,
  columns,
}: {
  selected: number[]
  onToggle: (value: number) => void
  items: ListDevelopmentFormModel[]
  columns?: number
}) => {
  const checkboxOptions = useMemo(() => {
    return items.map((developmentForm) => ({
      label: developmentForm.name,
      tooltipText: developmentForm.tooltipText,
      value: developmentForm.id,
    }))
  }, [items])

  return (
    <CheckboxGroup
      options={checkboxOptions}
      columns={columns}
      selected={selected}
      onToggle={onToggle}
    />
  )
}
